import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Trash2, Calendar, Phone, FileText, AlertTriangle, Clock, CheckCircle, XCircle } from '../../icons/wp-icons';
import { notificationApi } from '../../api/notificationApi';
import { terminBestaetingApi, kvaBestaetingApi } from '../../api/systemSettingsApi';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import toast from 'react-hot-toast';
import { useNotifications } from '../../context/NotificationContext';
import CounterProposalModal from './CounterProposalModal';

export default function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  // IDs von Notifications, für die aktuell ein Action-Request läuft (Doppelklick-Schutz).
  const [processingIds, setProcessingIds] = useState(() => new Set());
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Globalen NotificationContext verwenden für Tab-Titel Updates
  const { unreadCount, decrementUnread, clearUnread, refreshUnreadCount } = useNotifications();

  const isProcessing = (id) => processingIds.has(id);
  const withProcessingLock = async (id, fn) => {
    if (processingIds.has(id)) return; // Doppelklick verhindern
    setProcessingIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      return await fn();
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Load notifications when opening
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Unread count wird jetzt vom globalen NotificationContext verwaltet
  // Kein lokales Polling mehr nötig

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationApi.getAll();
      setNotifications(data.notifications);
      // Unread count wird vom globalen Context verwaltet
    } catch (error) {
      console.error('Load notifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      decrementUnread(); // Globalen Context updaten (inkl. Tab-Titel)
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      clearUnread(); // Globalen Context updaten (inkl. Tab-Titel)
    } catch (error) {
      console.error('Mark all as read error:', error);
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    try {
      const wasUnread = notifications.find(n => n.id === id)?.read === false;
      await notificationApi.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) {
        decrementUnread(); // Globalen Context updaten (inkl. Tab-Titel)
      }
    } catch (error) {
      console.error('Delete notification error:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Immer zur Benachrichtigungsseite navigieren für die Vollansicht
    navigate('/benachrichtigungen');
    setIsOpen(false);
  };

  const handleAction = async (notification, action, e) => {
    e?.stopPropagation();
    try {
      // Fallback: Actions ohne endpoint routen auf den Standard-Confirm/Reject-Flow,
      // damit AENDERUNG_TELEFON & Co. nicht am fehlenden action.endpoint crashen.
      if (!action?.endpoint) {
        if (action?.action === 'confirm') return await handleConfirmAppointment(notification, e);
        if (action?.action === 'reject')  return await handleRejectAppointment(notification, e);
        console.warn('Action ohne endpoint und ohne confirm/reject-Fallback:', action);
        return;
      }
      if (action.endpoint.startsWith('/api/')) {
        await apiClient.post(action.endpoint);
      } else if (action.endpoint.startsWith('tel:')) {
        window.location.href = action.endpoint;
      } else {
        navigate(action.endpoint);
      }

      if (!notification.read) {
        handleMarkAsRead(notification.id);
      }

      setIsOpen(false);
    } catch (error) {
      console.error('Action error:', error);
      alert('Fehler beim Ausführen der Aktion');
    }
  };

  // Prüfen ob Notification eine Terminanfrage ist (Bestätigen/Ablehnen)
  // Buttons werden auch nach Lesen angezeigt, bis Entscheidung getroffen wurde
  const isAppointmentSuggestion = (notification) => {
    // 🔄-Prefix-Check ENTFERNT: AENDERUNG_TELEFON-Notifications nutzen 🔄 inhaltlich als
    // Icon ("Änderung"), nicht als Status-Marker. data.aktion ist die Single-Source-of-Truth.
    return (notification.type === 'TERMIN_BESTAETIGUNG_ANFRAGE' ||
            notification.type === 'TERMIN_AENDERUNG_ANFRAGE' ||
            notification.type === 'TERMIN_STORNIERUNG_ANFRAGE') &&
           notification.data?.aktion === 'bestaetigen_oder_ablehnen' &&
           !notification.title.startsWith('✅') &&
           !notification.title.startsWith('❌');
  };

  // Prüfen ob Notification eine KVA-Bestätigung ist (Buttons auch nach Lesen anzeigen)
  const isKvaBestaetigung = (notification) => {
    return notification.type === 'KVA_BESTAETIGUNG_ANFRAGE' &&
           notification.data?.aktion === 'bestaetigen_oder_ablehnen' &&
           !notification.title.startsWith('✅') &&
           !notification.title.startsWith('❌');
  };

  // Prüfen ob Notification eine AB-Bestätigung ist
  const isAbBestaetigung = (notification) => {
    return notification.type === 'AB_ERSTELLT' &&
           notification.data?.aktion === 'bestaetigen_oder_ablehnen' &&
           !notification.title.startsWith('✅') &&
           !notification.title.startsWith('❌');
  };

  // Button-Labels basierend auf Typ
  const getActionLabels = (notification) => {
    const typ = notification.data?.typ;
    if (typ === 'STORNIERUNG') {
      return { confirm: 'Stornieren', reject: 'Ablehnen' };
    }
    if (typ === 'TERMIN_AENDERUNG') {
      return { confirm: 'Bestätigen', reject: 'Ablehnen' };
    }
    return { confirm: 'Bestätigen', reject: 'Ablehnen' };
  };

  // Terminanfrage bestätigen
  const handleConfirmAppointment = async (notification, e) => {
    e?.stopPropagation();
    await withProcessingLock(notification.id, async () => {
      try {
        await apiClient.post(`/notifications/${notification.id}/confirm`);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '✅ ' + n.title.replace(/^📅 |^🔄 |^🚫 /, '') }
            : n
          )
        );
        decrementUnread();
        const data = notification.data || {};
        const isTelefon = ['telefonassistent', 'elevenlabs'].includes((data.quelle || '').toLowerCase()) || data.typ?.includes('TELEFON');
        const isWhatsApp = data.quelle === 'whatsapp';
        toast.success(isTelefon
          ? 'Termin wurde bestätigt! Der Kunde wird telefonisch benachrichtigt.'
          : isWhatsApp
          ? 'Termin wurde bestätigt! Der Kunde wird per WhatsApp benachrichtigt.'
          : 'Termin wurde bestätigt! Der Kunde erhält eine Bestätigungs-E-Mail.');
      } catch (error) {
        console.error('Confirm appointment error:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // Terminanfrage ablehnen
  const handleRejectAppointment = async (notification, e) => {
    e?.stopPropagation();
    await withProcessingLock(notification.id, async () => {
      try {
        await apiClient.post(`/notifications/${notification.id}/reject`);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '❌ ' + n.title.replace(/^📅 |^🔄 |^🚫 /, '') }
            : n
          )
        );
        decrementUnread();
        const data = notification.data || {};
        const isTelefon = ['telefonassistent', 'elevenlabs'].includes((data.quelle || '').toLowerCase()) || data.typ?.includes('TELEFON');
        const isWhatsApp = data.quelle === 'whatsapp';
        toast.success(isTelefon
          ? 'Termin wurde abgelehnt. Der Kunde wird telefonisch benachrichtigt.'
          : isWhatsApp
          ? 'Termin wurde abgelehnt. Der Kunde wird per WhatsApp informiert.'
          : 'Termin wurde abgelehnt. Der Kunde wurde informiert.');
      } catch (error) {
        console.error('Reject appointment error:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // Gegenvorschlag
  const [counterProposalNotification, setCounterProposalNotification] = useState(null);
  const [counterProposalLoading, setCounterProposalLoading] = useState(false);

  const handleCounterPropose = (notification, e) => {
    e?.stopPropagation();
    setCounterProposalNotification(notification);
  };

  const handleSubmitCounterProposal = async ({ datum, uhrzeit }) => {
    if (!counterProposalNotification) return;
    setCounterProposalLoading(true);
    try {
      await apiClient.post(`/notifications/${counterProposalNotification.id}/counter-propose`, { datum, uhrzeit });
      setNotifications(prev =>
        prev.map(n => n.id === counterProposalNotification.id
          ? { ...n, read: true, title: '🔄 ' + n.title.replace(/^📅 |^🔄 |^🚫 /, '') }
          : n
        )
      );
      decrementUnread();
      const data = counterProposalNotification.data || {};
      const isWhatsApp = data.quelle === 'whatsapp';
      toast.success(isWhatsApp
        ? `Gegenvorschlag (${datum} um ${uhrzeit} Uhr) per WhatsApp gesendet.`
        : `Gegenvorschlag (${datum} um ${uhrzeit} Uhr) per E-Mail gesendet.`);
      setCounterProposalNotification(null);
    } catch (error) {
      console.error('Counter-propose error:', error);
      toast.error('Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setCounterProposalLoading(false);
    }
  };

  // KVA bestätigen/genehmigen und per E-Mail senden
  const handleConfirmKva = async (notification, e) => {
    e?.stopPropagation();
    await withProcessingLock(notification.id, async () => {
      try {
        const kvaId = notification.data?.kvaId;
        const kvaBestaetungId = notification.data?.kvaBestaetungId;
        const quelle = notification.data?.quelle;
        const isAb = notification.type === 'AB_ERSTELLT';

        if (isAb && kvaId) {
          const channel = quelle === 'whatsapp' ? 'whatsapp' : 'email';
          await apiClient.post(`/kostenvoranschlaege/${kvaId}/send-ab`, { channel });
          await notificationApi.markAsRead(notification.id);
        } else if (kvaId) {
          const channel = quelle === 'whatsapp' ? 'whatsapp' : 'email';
          await apiClient.post(`/kostenvoranschlaege/${kvaId}/send`, { channel });
          await notificationApi.markAsRead(notification.id);
        } else if (kvaBestaetungId) {
          await kvaBestaetingApi.approve(kvaBestaetungId);
        } else {
          await apiClient.post(`/notifications/${notification.id}/confirm`);
        }

        const dokumentTyp = isAb ? 'AB' : 'KVA';
        const kanalText = quelle === 'whatsapp' ? 'per WhatsApp' : 'per E-Mail';
        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '✅ ' + n.title.replace(/^📄 /, '').replace(/^📋 /, '') }
            : n
          )
        );
        decrementUnread();
        toast.success(`${dokumentTyp} wurde ${kanalText} an den Kunden gesendet!`);
      } catch (error) {
        console.error('Confirm KVA error:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // KVA ablehnen
  const handleRejectKva = async (notification, e) => {
    e?.stopPropagation();
    const grund = prompt('Grund für Ablehnung (optional):');
    await withProcessingLock(notification.id, async () => {
      try {
        const kvaBestaetungId = notification.data?.kvaBestaetungId;
        const anfrageId = notification.data?.anfrageId;
        if (kvaBestaetungId) {
          await kvaBestaetingApi.reject(kvaBestaetungId, grund);
        } else if (anfrageId) {
          await kvaBestaetingApi.reject(anfrageId, grund);
        } else {
          await apiClient.post(`/notifications/${notification.id}/reject`, { grund });
        }
        setNotifications(prev =>
          prev.map(n => n.id === notification.id
            ? { ...n, read: true, title: '❌ ' + n.title.replace(/^💰 /, '') }
            : n
          )
        );
        decrementUnread();
        toast.success('KVA wurde abgelehnt.');
      } catch (error) {
        console.error('Reject KVA error:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
    });
  };

  // KVA bearbeiten - Als Entwurf erstellen und zur Bearbeitungsseite navigieren
  const handleEditKva = async (notification, e) => {
    e?.stopPropagation();
    const kvaId = notification.data?.kvaId;
    const kvaBestaetungId = notification.data?.kvaBestaetungId;

    if (kvaId) {
      // KVA existiert bereits, direkt zur Bearbeitungsseite
      navigate(`/kostenvoranschlaege/${kvaId}/bearbeiten`);
      setIsOpen(false);
    } else if (kvaBestaetungId) {
      // KVA-Bestätigung als Entwurf erstellen, dann zum Editor
      try {
        const response = await apiClient.post(`/kva-bestaetigung/${kvaBestaetungId}/approve-as-draft`);
        const newKvaId = response.data.kva?.id;
        if (newKvaId) {
          setNotifications(prev =>
            prev.map(n => n.id === notification.id
              ? { ...n, read: true, data: { ...n.data, kvaId: newKvaId } }
              : n
            )
          );
          decrementUnread();
          toast.success('KVA als Entwurf erstellt — Sie können ihn jetzt bearbeiten.');
          navigate(`/kostenvoranschlaege/${newKvaId}/bearbeiten`);
        } else {
          navigate('/kostenvoranschlaege');
        }
      } catch (error) {
        console.error('KVA als Entwurf erstellen fehlgeschlagen:', error);
        toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      }
      setIsOpen(false);
    } else {
      navigate('/kostenvoranschlaege');
      setIsOpen(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'text-red-600 bg-red-50';
      case 'HIGH': return 'text-orange-600 bg-orange-50';
      case 'NORMAL': return 'text-primary bg-primary/10';
      case 'LOW': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const WhatsAppIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );

  const getTypeIcon = (notification) => {
    const type = typeof notification === 'string' ? notification : notification?.type;
    const quelle = typeof notification === 'object' ? notification?.data?.quelle : null;

    // WhatsApp-Quelle: Immer WhatsApp-Icon zeigen
    if (quelle === 'whatsapp') {
      return <WhatsAppIcon className="w-5 h-5 text-green-600" />;
    }

    switch (type) {
      case 'TERMIN_BESTAETIGUNG_ANFRAGE':
      case 'TERMIN_AENDERUNG_ANFRAGE':
      case 'TERMIN_STORNIERUNG':
      case 'ERINNERUNG_TERMIN':
        return <Calendar className="w-5 h-5" />;
      case 'TERMIN_STORNIERUNG_ANFRAGE':
        return <Calendar className="w-5 h-5" />;
      case 'RUECKRUF_ANFRAGE':
        return <Phone className="w-5 h-5" />;
      case 'RECHNUNG_ERSTELLT':
        return <FileText className="w-5 h-5" />;
      case 'ZAHLUNG_UEBERFAELLIG':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  return (
    <>
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition"
      >
        <Bell className="w-6 h-6" />

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[500px] flex flex-col animate-fade-in">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="font-semibold text-gray-900">Benachrichtigungen</h3>
              {unreadCount > 0 && (
                <p className="text-sm text-gray-500">{unreadCount} ungelesen</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-primary hover:text-primary-dark transition p-1 rounded"
                  title="Alle als gelesen markieren"
                >
                  <CheckCheck className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                Laden...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Keine Benachrichtigungen</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition cursor-pointer ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getPriorityColor(notification.priority)}`}>
                        {getTypeIcon(notification)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className={`font-medium text-sm truncate ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></span>
                          )}
                        </div>

                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {notification.message}
                        </p>

                        {/* Terminanfrage Bestätigen/Ablehnen Buttons */}
                        {isAppointmentSuggestion(notification) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button
                              onClick={(e) => handleConfirmAppointment(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className={`flex items-center gap-1 px-3 py-1 text-xs font-medium text-white rounded-button transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                notification.data?.typ === 'STORNIERUNG'
                                  ? 'bg-danger hover:bg-red-700'
                                  : 'bg-success hover:bg-emerald-700'
                              }`}
                            >
                              <CheckCircle className="w-3 h-3" />
                              {isProcessing(notification.id) ? 'Lädt...' : getActionLabels(notification).confirm}
                            </button>
                            {notification.data?.typ !== 'STORNIERUNG' && (
                              <button
                                onClick={(e) => handleCounterPropose(notification, e)}
                                disabled={isProcessing(notification.id)}
                                className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Calendar className="w-3 h-3" />
                                Alternativ
                              </button>
                            )}
                            <button
                              onClick={(e) => handleRejectAppointment(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-gray-500 text-white rounded-button hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="w-3 h-3" />
                              {getActionLabels(notification).reject}
                            </button>
                            <button
                              onClick={(e) => handleDelete(notification.id, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-white text-gray-500 border border-gray-300 rounded-button hover:bg-gray-50 hover:text-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Manuell erledigt - Benachrichtigung verwerfen"
                            >
                              <Trash2 className="w-3 h-3" />
                              Verwerfen
                            </button>
                          </div>
                        )}

                        {/* KVA Bestätigen/Ablehnen/Bearbeiten Buttons */}
                        {isKvaBestaetigung(notification) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button
                              onClick={(e) => handleConfirmKva(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-success text-white rounded-button hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle className="w-3 h-3" />
                              {isProcessing(notification.id) ? 'Lädt...' : 'KVA senden'}
                            </button>
                            <button
                              onClick={(e) => handleEditKva(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FileText className="w-3 h-3" />
                              Bearbeiten
                            </button>
                            <button
                              onClick={(e) => handleRejectKva(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-gray-500 text-white rounded-button hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="w-3 h-3" />
                              Ablehnen
                            </button>
                            <button
                              onClick={(e) => handleDelete(notification.id, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-white text-gray-500 border border-gray-300 rounded-button hover:bg-gray-50 hover:text-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Manuell erledigt - Benachrichtigung verwerfen"
                            >
                              <Trash2 className="w-3 h-3" />
                              Verwerfen
                            </button>
                          </div>
                        )}

                        {/* AB Bestätigen/Bearbeiten/Ablehnen Buttons */}
                        {isAbBestaetigung(notification) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button
                              onClick={(e) => handleConfirmKva(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-success text-white rounded-button hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle className="w-3 h-3" />
                              {isProcessing(notification.id) ? 'Lädt...' : 'AB versenden'}
                            </button>
                            <button
                              onClick={(e) => handleEditKva(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FileText className="w-3 h-3" />
                              Bearbeiten
                            </button>
                            <button
                              onClick={(e) => handleRejectKva(notification, e)}
                              disabled={isProcessing(notification.id)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-gray-500 text-white rounded-button hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="w-3 h-3" />
                              Ablehnen
                            </button>
                            <button
                              onClick={(e) => handleDelete(notification.id, e)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-white text-gray-500 border border-gray-300 rounded-button hover:bg-gray-50 hover:text-gray-700 transition"
                              title="Manuell erledigt - Benachrichtigung verwerfen"
                            >
                              <Trash2 className="w-3 h-3" />
                              Verwerfen
                            </button>
                          </div>
                        )}

                        {/* Standard Actions - nur bei unread und keine Spezial-Buttons */}
                        {notification.actions && notification.actions.length > 0 && !notification.read && !isAppointmentSuggestion(notification) && !isKvaBestaetigung(notification) && !isAbBestaetigung(notification) && (
                          <div className="flex gap-2 mt-2">
                            {notification.actions.slice(0, 2).map((action, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => handleAction(notification, action, e)}
                                className="px-3 py-1 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(notification.createdAt)}
                          </span>

                          <div className="flex gap-1">
                            {!notification.read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(notification.id);
                                }}
                                className="p-1 text-gray-400 hover:text-primary transition rounded"
                                title="Als gelesen markieren"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(notification.id, e)}
                              className="p-1 text-gray-400 hover:text-red-600 transition rounded"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - View All */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  navigate('/benachrichtigungen');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-primary hover:text-primary-dark font-medium transition"
              >
                Alle Benachrichtigungen anzeigen
              </button>
            </div>
          )}
        </div>
      )}
    </div>

    <CounterProposalModal
      isOpen={!!counterProposalNotification}
      onClose={() => setCounterProposalNotification(null)}
      onSubmit={handleSubmitCounterProposal}
      isLoading={counterProposalLoading}
      originalTermin={counterProposalNotification?.data?.terminDaten
        ? `${counterProposalNotification.data.terminDaten.datum} um ${counterProposalNotification.data.terminDaten.uhrzeit} Uhr`
        : null}
    />
    </>
  );
}
